import React from 'react';
import { applyTypography, BaseButton } from '@repo/common';
import { ReactComponent as CancelIcon } from '@repo/common/assets/icons/20px/icon_20px_cancel.svg';

export const DefaultRefundPolicy = () => (
  <div
    className={`${applyTypography('b16')} text-text-primary flex flex-col gap-[6px] text-left`}
    style={{ lineHeight: 1.6 }}
  >
    <p>Refunds depend on when you cancel your booking.</p>
    <ul className='list-disc pl-[24px] flex flex-col gap-[2px]'>
      <li>7 or more days before the event: Full refund</li>
      <li>3–6 days before the event: 50% refund</li>
      <li>2 days or less before the event: Refunds may be limited</li>
      <li>No-show: If you are absent without notice, refunds may be limited</li>
    </ul>
    <p>
      If Koready cancels the event, all participants will receive a full refund.
      If Koready reschedules the event, you can either accept the new date or
      receive a full refund.
    </p>
    <p>
      Need a different date? You can move your booking to another available date
      up to 7 days before the event.
    </p>

    <p className={`${applyTypography('b16Strong')} mt-[10px]`}>
      How to request a refund
    </p>
    <p>You can request a refund from your account.</p>
    <p>Go to My Page &gt; Payments &gt; View invoice &gt; Request a refund.</p>
    <p>Then, select the Reason for Refund and submit your request.</p>

    <p className={`${applyTypography('b16Strong')} mt-[10px]`}>
      What happens next
    </p>
    <ol className='list-decimal pl-[24px] flex flex-col gap-[2px]'>
      <li>We will review your request.</li>
      <li>We will contact you within 3 business days.</li>
      <li>
        If approved, we will process your refund to your original payment
        method.
      </li>
    </ol>
    <p>
      Some cases may be reviewed as exceptions, including duplicate payments,
      technical issues, issues caused by Koready, or cases required by
      applicable law.
    </p>
    <p>
      For more details, please see{' '}
      <a
        href='/refund-policy'
        target='_blank'
        rel='noopener noreferrer'
        className='underline text-text-primary'
      >
        View full refund policy
      </a>
      .
    </p>
  </div>
);

/* ── 워크숍 전용 정책(마크다운 비슷한 텍스트) 렌더 ─────────────────
 * 어드민 폼에 붙여 넣은 정책 원문이 `#`, `##`, `-`, `1.`, `**굵게**` 를 쓰므로
 * 그 정도만 해석한다. 이런 표시가 하나도 없으면 예전처럼 줄바꿈 보존 텍스트로 그린다
 * (다른 워크숍의 기존 정책 문구가 달라 보이지 않게).
 */
const MARKDOWN_HINT = /(^|\n)\s*(#{1,3}\s|[-*]\s|\d+\.\s)|\*\*[^*\n]+\*\*/;

export const looksLikeMarkdown = (text) => MARKDOWN_HINT.test(String(text || ''));

const renderInline = (text, keyPrefix) => {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={`${keyPrefix}-b${index}`} className='font-semibold'>
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <React.Fragment key={`${keyPrefix}-t${index}`}>{part}</React.Fragment>;
  });
};

const parseBlocks = (source) => {
  const lines = String(source || '').replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  let paragraph = [];
  let list = null; // { type: 'ul' | 'ol', items: [[line, line...]] }

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({ type: 'p', lines: paragraph });
      paragraph = [];
    }
  };
  const flushList = () => {
    if (list) {
      blocks.push(list);
      list = null;
    }
  };

  for (const raw of lines) {
    const line = raw.replace(/\s+$/, '');
    const trimmed = line.trim();

    if (!trimmed) {
      flushParagraph();
      // 리스트 아이템 사이의 빈 줄은 리스트를 끊지 않는다 (붙여 넣은 정책이 그런 모양이다)
      continue;
    }

    const heading = trimmed.match(/^(#{1,3})\s+(.*)$/);
    if (heading) {
      flushParagraph();
      flushList();
      blocks.push({ type: `h${heading[1].length}`, text: heading[2] });
      continue;
    }

    const bullet = trimmed.match(/^[-*]\s+(.*)$/);
    const numbered = trimmed.match(/^\d+\.\s+(.*)$/);
    if (bullet || numbered) {
      flushParagraph();
      const type = bullet ? 'ul' : 'ol';
      if (!list || list.type !== type) {
        flushList();
        list = { type, items: [] };
      }
      list.items.push([(bullet || numbered)[1]]);
      continue;
    }

    // 들여쓴 줄이 리스트 바로 아래 오면 그 아이템의 설명으로 붙인다
    if (list && /^\s{2,}/.test(line)) {
      list.items[list.items.length - 1].push(trimmed);
      continue;
    }

    flushList();
    paragraph.push(trimmed);
  }
  flushParagraph();
  flushList();
  return blocks;
};

export const PolicyMarkdown = ({ content }) => {
  const blocks = parseBlocks(content);
  return (
    <div
      className={`${applyTypography('b16')} text-text-primary flex flex-col gap-[8px] text-left`}
      style={{ lineHeight: 1.6 }}
    >
      {blocks.map((block, index) => {
        const key = `blk-${index}`;
        if (block.type === 'h1') {
          return (
            <p key={key} className={`${applyTypography('title2')} mt-[4px]`}>
              {renderInline(block.text, key)}
            </p>
          );
        }
        if (block.type === 'h2' || block.type === 'h3') {
          return (
            <p key={key} className={`${applyTypography('b16Strong')} mt-[10px]`}>
              {renderInline(block.text, key)}
            </p>
          );
        }
        if (block.type === 'ul' || block.type === 'ol') {
          const Tag = block.type;
          return (
            <Tag
              key={key}
              className={`${
                block.type === 'ul' ? 'list-disc' : 'list-decimal'
              } pl-[24px] flex flex-col gap-[4px]`}
            >
              {block.items.map((item, itemIndex) => (
                <li key={`${key}-i${itemIndex}`}>
                  {renderInline(item[0], `${key}-i${itemIndex}`)}
                  {item.slice(1).map((sub, subIndex) => (
                    <span
                      key={`${key}-i${itemIndex}-s${subIndex}`}
                      className='block text-text-secondary'
                    >
                      {renderInline(sub, `${key}-i${itemIndex}-s${subIndex}`)}
                    </span>
                  ))}
                </li>
              ))}
            </Tag>
          );
        }
        return (
          <p key={key}>
            {block.lines.map((line, lineIndex) => (
              <React.Fragment key={`${key}-l${lineIndex}`}>
                {lineIndex > 0 && <br />}
                {renderInline(line, `${key}-l${lineIndex}`)}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
};

/**
 * 모달 본문과 어드민 폼 미리보기가 같은 렌더러를 쓴다.
 * content 가 있으면 워크숍 전용 정책(마크다운이면 해석), 없으면 기본 Koready 정책.
 */
export const RefundPolicyContent = ({ content = '', emptyText }) => {
  if (content) {
    return looksLikeMarkdown(content) ? (
      <PolicyMarkdown content={content} />
    ) : (
      <p
        className={`${applyTypography('b16Strong')} text-text-primary whitespace-pre-wrap`}
        style={{ lineHeight: '1.6' }}
      >
        {content}
      </p>
    );
  }
  if (emptyText) {
    return (
      <p
        className={`${applyTypography('b16')} text-text-secondary`}
        style={{ lineHeight: '1.6' }}
      >
        {emptyText}
      </p>
    );
  }
  return <DefaultRefundPolicy />;
};

const RefundModal = ({
  isOpen,
  onClose,
  content = '',
  title = 'Cancellation & Refund Policy',
  emptyText,
}) => {
  if (!isOpen) return null;

  const handleClose = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    onClose && onClose();
  };

  const handleOverlayClick = (e) => {
    handleClose(e);
  };

  return (
    <>
      <div
        className='fixed inset-0 bg-surface-black40 z-[10000000000]'
        onClick={handleOverlayClick}
      />
      {/*
        바깥 박스는 뷰포트에 고정되고 안쪽 카드는 그 높이를 넘지 못한다(max-h-full).
        본문만 스크롤되게 flex-1 + min-h-0 + overflow-y-auto 를 준다 — 이전에는 카드 높이가 900px 고정이라
        낮은 화면에서 헤더·닫기 버튼이 화면 밖으로 밀려나고 페이지 전체가 스크롤됐다.
        여백·모서리·글자 간격은 모바일에서 줄이고 tabletH 이상에서 기존 값으로 돌아간다.
      */}
      <div className='fixed inset-0 z-[10000000001] flex items-center justify-center px-[16px] py-[24px] tabletH:px-[80px] tabletH:py-[80px]'>
        <div
          role='dialog'
          aria-modal='true'
          aria-label={title}
          className='bg-grayscale-gray01 flex flex-col w-full max-w-[1166px] max-h-full min-h-[240px] overflow-hidden rounded-[20px] tabletH:rounded-[30px] shadow-2'
          onClick={(e) => e.stopPropagation()}
        >
          <div className='flex shrink-0 items-center justify-between gap-[8px] px-[16px] pt-[16px] pb-[12px] tabletH:px-[40px] tabletH:pt-[24px] tabletH:pb-[16px]'>
            <div className='flex items-center self-stretch w-8 shrink-0' />
            <p
              className={`${applyTypography(
                'b16Strong',
              )} flex-1 min-w-0 text-text-primary text-center`}
              style={{ lineHeight: '1.5' }}
            >
              {title}
            </p>
            <div className='flex items-center justify-end w-8 shrink-0 rounded-[50px] bg-grayscale-gray02'>
              <BaseButton
                type='text'
                size='s'
                icon={<CancelIcon className='w-4 h-4 text-icon-primary' />}
                aria-label='Close'
                onClick={handleClose}
              />
            </div>
          </div>

          <div className='flex-1 min-h-0 w-full overflow-y-auto overscroll-contain px-[16px] pb-[24px] tabletH:px-[40px] tabletH:pb-[40px]'>
            <RefundPolicyContent content={content} emptyText={emptyText} />
          </div>
        </div>
      </div>
    </>
  );
};

export default RefundModal;
